import { useState, useEffect, useCallback, useRef } from 'react';
import { GERMANY } from '../../../../constants';
import { logAmplitudeEvent } from '../../../../common/services/amplitudeService';
import { buildListGroups } from './buildListGroups';

export const useAddressFields = ({
    transfer,
    name,
    updateTransfer,
    t,
    search: searchProp,
    getPlace: getPlaceProp,
    searchPlaceId: searchPlaceIdProp,
    language,
}) => {
    const address = transfer[name];
    const [listGroups, setListGroups] = useState([]);
    const [errorMessage, setErrorMessage] = useState(null);
    const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
    const cancelRef = useRef(null);
    const debounceRef = useRef(null);
    const selectionSeqRef = useRef(0);

    const listOnSelect = (item) => {
        // Each selection gets a token; if the user picks another entry while the
        // async place/DB lookups are still in flight, the stale completion is
        // ignored so it can't overwrite the newer selection.
        const selectionToken = (selectionSeqRef.current += 1);
        const isStaleSelection = () => selectionToken !== selectionSeqRef.current;

        if (!item.placeId) {
            updateTransfer({
                name,
                value: { ...item, countryId: GERMANY, placeValue: item.placeValue || item.label, label: undefined },
            });
            setErrorMessage(null);
            return;
        }

        getPlaceProp(item.placeId).then((data) => {
            if (isStaleSelection()) {
                return;
            }

            const isStreetMissing = data.street === '--';
            const isZipMissing = !data.zip;
            const isZipIncomplete = !isZipMissing && data.zip.length !== 5;

            if (isStreetMissing || isZipMissing || isZipIncomplete) {
                searchPlaceIdProp(data.id).then((dbResult) => {
                    if (isStaleSelection()) {
                        return;
                    }

                    if (dbResult) {
                        setErrorMessage(null);
                        updateTransfer({
                            name,
                            value: {
                                ...data,
                                name: data.name !== data.street ? data.name : '',
                                countryId: GERMANY,
                                placeValue: item.placeValue || item.label,
                                gPlaceId: item.placeId,
                                label: undefined,
                            },
                        });
                    } else if (isStreetMissing) {
                        // Log to Amplitude with full address details
                        logAmplitudeEvent('Place ID: Missing Street', {
                            placeId: item.placeId,
                            address: data,
                            errorType: 'missing_street',
                        });
                        setErrorMessage('booking.errorStreetMissing');
                        updateTransfer({ name, value: { ...address, placeValue: '' } });
                    } else if (isZipMissing) {
                        // Log to Amplitude with full address details
                        logAmplitudeEvent('Place ID: Missing Zip', {
                            placeId: item.placeId,
                            address: data,
                            errorType: 'missing_zip',
                        });
                        setErrorMessage('booking.errorZipMissing');
                        updateTransfer({ name, value: { ...address, placeValue: '' } });
                    } else {
                        setErrorMessage('booking.errorZipIncomplete');
                    }
                });
            } else {
                setErrorMessage(null);
                updateTransfer({
                    name,
                    value: {
                        ...data,
                        name: data.name !== data.street ? data.name : '',
                        countryId: GERMANY,
                        placeValue: item.placeValue || item.label,
                        gPlaceId: item.placeId,
                        label: undefined,
                    },
                });
            }
        });
    };

    const search = useCallback(
        (q) => {
            if (!searchProp) return;

            if (!q || q.length < 2) {
                setListGroups([]);
                return;
            }

            clearTimeout(debounceRef.current);

            debounceRef.current = setTimeout(() => {
                cancelRef.current?.();

                const placesRequest = searchProp(q, language);
                cancelRef.current = placesRequest.cancel;

                const timeoutId = setTimeout(() => setShowLoadingSpinner(true), 1500);

                placesRequest.then((placesData) => {
                    cancelRef.current = null;
                    clearTimeout(timeoutId);
                    setShowLoadingSpinner(false);
                    setListGroups(buildListGroups({ t, placesData }));
                });
            }, 300);
        },
        [searchProp, t, language],
    );

    useEffect(() => {
        if (address.placeValue) {
            search(address.placeValue);
        } else {
            setListGroups([]);
        }
    }, [address.placeValue, search]);

    return {
        t: t || ((key) => key),
        address,
        listGroups,
        errorMessage,
        showLoadingSpinner,
        setErrorMessage,
        listOnSelect,
    };
}; 